import { FormEvent, useMemo, useState } from 'react';
import useTranslation from '../../customHooks/useTranslation';
import useLanguage from '../../customHooks/useLanguage';
import { upcomingWorkdays, useBooking } from '../../contextApi/BookingContext';
import { BOOKING_URL, EMAIL, PHONE, PHONE_HREF } from '../../config/site';

// The first step of every engagement, in working form: pick a day and a time window.
// With a booking tool configured it hands over to the calendar; otherwise the
// proposal travels to the brief form, where the visitor adds contact details.
export default function BookingPanel() {
	const t = useTranslation();
	const { language } = useLanguage();
	const { slot, setSlot } = useBooking();
	const days = useMemo(() => upcomingWorkdays(5), []);
	// Defaults: the second working day, 12:00–16:00.
	const [day, setDay] = useState<number>(() => {
		const picked = slot.day ? days.findIndex(d => d.getTime() === slot.day?.getTime()) : -1;
		return picked >= 0 ? picked : 1;
	});
	const [window_, setWindow] = useState<number>(() => {
		const picked = slot.window ? t.hero.booking.windows.indexOf(slot.window) : -1;
		return picked >= 0 ? picked : 1;
	});
	const locale = language === 'pl' ? 'pl-PL' : 'en-GB';

	const submit = (e: FormEvent) => {
		e.preventDefault();
		setSlot({ day: days[Math.max(day, 0)], window: t.hero.booking.windows[Math.max(window_, 0)] });
		if (BOOKING_URL) {
			window.open(BOOKING_URL, '_blank', 'noopener');
			return;
		}
		document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
		window.setTimeout(() => document.getElementById('brief-name')?.focus({ preventScroll: true }), 600);
	};

	return (
		<form id='book' onSubmit={submit} className='scroll-mt-28 bg-sheet-deep/80 p-5 sm:p-6' aria-labelledby='book-title'>
			<h2 id='book-title' className='type-wide text-lg font-bold tracking-[-0.01em] text-ink'>
				{t.hero.booking.title}
			</h2>

			<fieldset className='mt-5'>
				<legend className='lettering text-label text-ink-faint'>{t.hero.booking.day}</legend>
				<div className='mt-2 grid grid-cols-5 gap-1.5'>
					{days.map((d, i) => (
						<label key={d.toISOString()} className='group relative cursor-pointer'>
							<input type='radio' name='day' className='peer sr-only' checked={day === i} onChange={() => setDay(i)} />
							<span className='flex flex-col items-center border border-ink-line py-2 text-ink-soft transition-colors group-hover:border-ink-soft peer-checked:border-redline peer-checked:bg-redline peer-checked:text-sheet-deep peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-redline'>
								<span className='lettering text-[0.66rem]'>{d.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '')}</span>
								<span className='text-lg font-bold leading-tight'>{d.getDate()}</span>
								<span className='lettering text-[0.62rem] opacity-80'>{d.toLocaleDateString(locale, { month: 'short' }).replace('.', '')}</span>
							</span>
						</label>
					))}
				</div>
			</fieldset>

			<fieldset className='mt-4'>
				<legend className='lettering text-label text-ink-faint'>{t.hero.booking.time}</legend>
				<div className='mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4'>
					{t.hero.booking.windows.map((w, i) => (
						<label key={w} className='group relative cursor-pointer'>
							<input type='radio' name='window' className='peer sr-only' checked={window_ === i} onChange={() => setWindow(i)} />
							<span className='block border border-ink-line py-2 text-center text-sm text-ink-soft transition-colors group-hover:border-ink-soft peer-checked:border-redline peer-checked:text-redline peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-redline'>
								{w}
							</span>
						</label>
					))}
				</div>
			</fieldset>

			<button type='submit' className='btn-redline mt-5 w-full'>
				{BOOKING_URL ? t.hero.booking.submitExternal : t.hero.booking.submit}
				<svg viewBox='0 0 16 16' className='h-4 w-4' aria-hidden='true'>
					<path d='M2 8h11M9 4l4 4-4 4' fill='none' stroke='currentColor' strokeWidth='1.6' />
				</svg>
			</button>
			<p className='mt-3 text-sm text-ink-faint'>{t.hero.booking.note}</p>

			<p className='mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-ink-line pt-4 text-sm text-ink-soft'>
				<span>{t.hero.booking.orDirect}</span>
				<a href={PHONE_HREF} className='font-semibold text-ink underline decoration-ink-line hover:decoration-redline'>
					{PHONE}
				</a>
				<a href={`mailto:${EMAIL}`} className='font-semibold text-ink underline decoration-ink-line hover:decoration-redline'>
					{EMAIL}
				</a>
			</p>
		</form>
	);
}
